import { v4 } from 'uuid'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import { DATE_FORMATS, StudentTagList } from 'src/config/constants'
import type { IBaseLocation, IBasePerson } from 'src/interfaces/common'
import exportFromJSON from 'export-from-json'
import type { IAttendee } from 'src/interfaces/attendance'
import { RRule, RRuleSet } from 'rrule'
import { isReactive, toRaw } from 'vue'

dayjs.extend(relativeTime)

interface IScrollTop {
  top?: number
  left?: number
}

export const capitalize = (s: string) => {
  if (typeof s !== 'string') return ''
  return s.charAt(0).toUpperCase() + s.slice(1)
}

export const scrollTop = ({ top, left }: IScrollTop) => {
  window.scrollTo({ top, left, behavior: 'smooth' })
}

export const getShorterName = (val: string, length = 30) => {
  if (val.length > length) return val.substring(0, length - 3) + '...'
  return val
}

export const getAvatarInitial = (person: IBasePerson | IAttendee) => {
  return person.preferredName
    ? person.preferredName.trim()[0]
    : (person.firstName?.trim()[0] ?? '')
}

// Up to 2 uppercase initials from a full name, e.g. "Test Test (Mom)" -> "TT".
// Strips a trailing bracketed suffix so relationship labels don't get counted as name parts.
export const getInitials = (name?: string): string => {
  if (!name) return '?'
  const cleaned = name.replace(/\s*\([^)]*\)\s*$/, '')
  return cleaned
    .split(' ')
    .map((n) => n[0])
    .join('')
    .substring(0, 2)
    .toUpperCase()
}

export const displayName = (
  entity: IBasePerson | IAttendee | null,
  showFullName = false,
  length: number | null = 15,
  lastNameFirst = false,
  showLastInitial = false,
): string => {
  if (!entity) return ''

  // Properly Capitalize Names
  const firstNameProper = entity.firstName
    ? entity.firstName[0].toUpperCase().toString() +
      entity.firstName.substring(1)
    : ''
  const lastNameProper = entity.lastName
    ? entity.lastName[0].toUpperCase().toString() + entity.lastName.substring(1)
    : ''
  let name = ''

  // Find correct orientation/combination of names
  if (!showFullName && !lastNameFirst) name = firstNameProper
  if (!showFullName && lastNameFirst) name = lastNameProper
  if (showFullName && !lastNameFirst)
    name = [firstNameProper, lastNameProper].filter(Boolean).join(' ')
  if (showFullName && lastNameFirst)
    name =
      lastNameProper +
      (firstNameProper && lastNameProper ? ', ' : '') +
      firstNameProper

  // If this statement is true all statements above result in an empty string so look at other fields
  if (!name) {
    if (firstNameProper) name = firstNameProper
    else if (entity.name) name = entity.name
    else if (entity.label) name = entity.label
    else name = lastNameProper
  }

  if (showLastInitial && !showFullName && !lastNameFirst) {
    name += ` ${lastNameProper[0]}`
  }

  // Return name shortened to acceptable length
  if (length) {
    name = name.length > length ? name.substring(0, length - 2) + '...' : name
  }
  return name
}

export const displayAddress = (entity: IBaseLocation) => {
  if (!entity) return ''
  const tokens = []
  //@ts-expect-error TODO fix later
  if (entity.address) tokens.push(capitalize(entity.address))
  //@ts-expect-error TODO fix later
  if (entity.city) tokens.push(capitalize(entity.city))
  //@ts-expect-error TODO fix later
  if (entity.state) tokens.push(capitalize(entity.state))
  //@ts-expect-error TODO fix later
  if (entity.country) tokens.push(capitalize(entity.country))
  return tokens.join(', ')
}

export const formatSIN = (SIN: string) => {
  if (SIN) {
    const SINArray = SIN.split('')
    if (SINArray.length === 9) {
      SINArray.splice(3, 0, '-')
      SINArray.splice(7, 0, '-')
    }
    return SINArray.join('')
  }
}

export const formatCurrency = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
})

export const toCurrency = (value: number) => formatCurrency.format(value)

export const postalCodeValidator = (val: string) => {
  if (val) {
    const trimmed = val.trim()
    const re =
      /^[ABCEGHJ-NPRSTVXY]\d[ABCEGHJ-NPRSTV-Z][ -]?\d[ABCEGHJ-NPRSTV-Z]\d$/i
    return re.test(trimmed.toLowerCase()) || 'Please enter a valid postal code'
  }
  return true
}

function normalizePostalCodeCountry(country?: string | null) {
  if (country === 'USA' || country === 'US') return 'US'
  if (country === 'CA') return 'CA'
  return ''
}

export function getPostalCodeFormatHint(
  postalCode?: string | null,
  country?: string | null,
) {
  const value = postalCode?.trim()
  if (!value) return ''

  const normalizedCountry = normalizePostalCodeCountry(country)

  if (normalizedCountry === 'CA') {
    const canadianPostalCodePattern =
      /^(?!.*[DFIOQU])[A-VXY]\d[A-Z][ -]?\d[A-Z]\d$/i

    return canadianPostalCodePattern.test(value)
      ? ''
      : 'Enter a valid Canadian postal code'
  }

  if (normalizedCountry === 'US') {
    const usZipCodePattern = /^\d{5}([ -]?\d{4})?$/

    return usZipCodePattern.test(value)
      ? ''
      : 'Use ZIP code format 12345 or 12345-1234'
  }

  return ''
}

export const displayDate = (date: string) => {
  if (date) return dayjs(date).utc().format(DATE_FORMATS.humanreadable)
  else return 'invalid date'
}

export const displayRelativeTime = (date?: Date | string) => {
  if (!date) return ''
  return dayjs(date).fromNow()
}

export const displayDateNonUTC = (date: string) => {
  if (date) return dayjs(date).format(DATE_FORMATS.humanreadable)
  else return 'invalid date'
}

// Parse date string trying explicit formats first for Safari compatibility
export const parseDateSafe = (date: string) => {
  const parsed = dayjs(
    date,
    [
      DATE_FORMATS.datetime,
      DATE_FORMATS.dateTimeHumanReadable,
      DATE_FORMATS.datetime2,
      DATE_FORMATS.humanreadable,
    ],
    true,
  )
  return parsed.isValid() ? parsed : dayjs(date)
}

export const displayDateSlash = (date: string) => {
  if (date) return parseDateSafe(date).format(DATE_FORMATS.dateSlash)
  else return 'invalid date'
}

export const displayDateHumanReadable = (date: string) => {
  if (date) return dayjs(date).format(DATE_FORMATS.humanreadable)
  else return 'invalid date'
}

export const displayDateTimeHumanReadable = (date: string) => {
  return parseDateSafe(date).format(DATE_FORMATS.dateTimeHumanReadable)
}

export const displayTime = (time: string, timeZone?: string) => {
  if (!time) return '-'
  return time
    .split(',')
    .map((subTime) => {
      let temp = dayjs('1/1/1 ' + subTime).format(DATE_FORMATS.shortTime) ?? '-'
      if (timeZone) temp += ` (${timeZone})`
      return temp
    })
    .join(', ')
}

export const timeStringToSeconds = (time: string) => {
  const [timeStr, prefix] = time.split(' ')
  let runningTime = 0

  const timeArray = timeStr.split(':')
  let hours = parseInt(timeArray[0])
  const minutes = parseInt(timeArray[1])
  const seconds = timeArray.length === 3 ? parseInt(timeArray[2]) : 0

  if (prefix?.toLowerCase() === 'pm') {
    if (hours !== 12) hours += 12
  } else if (hours === 12) {
    hours = 0
  }

  runningTime = hours * 3600 + minutes * 60 + seconds
  return runningTime
}

export const requireValidator = (val: string) => {
  const trimmed = val.trim()
  return trimmed !== null && trimmed !== undefined && trimmed.length > 0
}

export const requiredField = (val: string, message?: string) => {
  return (
    (!!val && val.trim().length > 0) ||
    (!!message && message.length > 0 ? message : 'Required')
  )
}

export function createRuleSet(recurrenceRule: string): RRuleSet {
  const ruleSet = new RRuleSet()
  let dtstart: Date | undefined | null

  const lines = recurrenceRule.split('\n')

  for (const line of lines) {
    if (line.startsWith('DTSTART:')) {
      dtstart = RRule.parseString(line).dtstart
    } else if (line.startsWith('RRULE:')) {
      const options = RRule.parseString(line.replace('RRULE:', ''))
      if (dtstart) {
        options.dtstart = dtstart
      }
      const rule = new RRule(options)
      ruleSet.rrule(rule)
    } else if (line.startsWith('EXDATE:')) {
      const dates = line.replace('EXDATE:', '').split(',')
      dates.forEach((date) => {
        const parsedDate = parseRfc5545Date(date)
        if (parsedDate) {
          ruleSet.exdate(parsedDate)
        }
      })
    }
  }

  return ruleSet
}

export function parseRfc5545Date(rfcDate: string): Date | null {
  try {
    // Convert "20241215T000000Z" to "2024-12-15T00:00:00Z"
    const isoDate = rfcDate.replace(
      /^(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})Z$/,
      '$1-$2-$3T$4:$5:$6Z',
    )
    const parsedDate = new Date(isoDate)

    if (isNaN(parsedDate.getTime())) {
      throw new Error(`Invalid date: ${rfcDate}`)
    }

    return parsedDate
  } catch {
    return null
  }
}

export const validateDate = (val: string) => {
  if (val) {
    if (val.length === 11) {
      return (
        dayjs(val, DATE_FORMATS.humanreadable).format(DATE_FORMATS.humanreadable) ===
          val ||
        'Please enter a valid date'
      )
    }
    return 'Please enter a valid date'
  }
}

export const validateMaxAmount = (val: number, maxAmount: number) => {
  return val <= maxAmount || `The maximum amount allowed is ${maxAmount}`
}

export const validateMinAmount = (val: number, minAmount: number) => {
  return (
    val >= minAmount ||
    `Please enter a value larger than or equal to ${minAmount}`
  )
}

export const validateMaxCharacters = (val: string, length: number) => {
  if (typeof val === 'number') val = (val as number).toString()
  if (!length || !val) return true
  return val.length <= length || `Please use maximum ${length} characters`
}

export const validateDateTime = (val: string) => {
  if (val) {
    if (val.length === 20) {
      return (
        dayjs(val, 'MMM D YYYY hh:mm A').isValid() ||
        'Please enter a valid date time'
      )
    }
    return 'Please enter a valid date time'
  }
}

export const validateDateTimeAmPm = (val: string) => {
  if (val) {
    if (val.length === 19) {
      return (
        dayjs(val, DATE_FORMATS.datetime).isValid() ||
        'Please enter a valid date time in "YYYY-MM-DD hh:mm AM/PM" format'
      )
    }
    return 'Please enter a valid date time in "YYYY-MM-DD hh:mm AM/PM" format'
  }
}

export const emailValidator = (val: string) => {
  if (val) {
    const trimmed = val.trim()
    const re =
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/

    if (/[A-Z]/.test(trimmed)) {
      return 'Please enter a valid email address with no uppercase letters'
    }

    return (
      re.test(trimmed.toLowerCase()) || 'Please enter a valid email address'
    )
  }
  return true
}

export const calculateStudentAgeInMonths = (
  val: string,
  startDate?: string,
) => {
  const Bday = dayjs(val)
  let Cday
  if (!startDate) {
    Cday = dayjs()
  } else {
    Cday = dayjs(startDate)
  }
  let ageInMonths = 0
  if (
    Cday.year() - Bday.year() > 0 ||
    (Cday.year() - Bday.year() === 0 &&
      (Bday.month() < Cday.month() ||
        (Bday.month() === Cday.month() && Bday.date() <= Cday.date())))
  ) {
    const age = Cday.year() - Bday.year()
    ageInMonths = age * 12
    if (Cday.month() >= Bday.month()) {
      if (Cday.date() >= Bday.date()) {
        ageInMonths = ageInMonths + Cday.month() - Bday.month()
      } else {
        ageInMonths = ageInMonths + Cday.month() - Bday.month() - 1
      }
    } else {
      ageInMonths -= 12
      if (Cday.date() >= Bday.date()) {
        ageInMonths =
          ageInMonths + (12 - (Bday.month() + 1)) + (Cday.month() + 1)
      } else {
        ageInMonths =
          ageInMonths + (12 - (Bday.month() + 1)) + (Cday.month() + 1) - 1
      }
    }
  }
  return ageInMonths
}

// A child may be pre-registered with a future date of birth, so clamp to 0
// instead of letting dayjs().diff return a negative value.
export const calculateAgeInYears = (dateOfBirth: string | Date): number => {
  return Math.max(dayjs().diff(dayjs(dateOfBirth), 'year'), 0)
}

export const formatStudentAge = (studentAgeInMonths: number) => {
  const ageInYear =
    studentAgeInMonths > 12 ? Math.floor(studentAgeInMonths / 12) : 0
  const ageInMonths = studentAgeInMonths - 12 * ageInYear
  return `${ageInYear}-${ageInMonths}`
}

export const formatAgeYearsMonths = (studentAgeInMonths: number) => {
  const safeMonths = Math.max(studentAgeInMonths, 0)
  const years = Math.floor(safeMonths / 12)
  const months = safeMonths - years * 12
  return `${years}Y${months}M`
}

export const getStudentTagLabel = (status: string | null | undefined) => {
  if (!status) return null

  if (status === StudentTagList.toEnd) return 'TEn'
  if (status === StudentTagList.toWithdraw) return 'TWd'

  return status.substring(0, 3)
}

export const sortDatesArray = (
  field: string,
  arr: any,
  order: string,
  isTimeOnly = false,
) => {
  return arr.sort((a: any, b: any) => {
    const parseDate = (value: string) => {
      if (!value) return null
      if (isTimeOnly) {
        const formattedTime = value.replace(/(AM|PM)$/, ' $1')
        const date = new Date(`1970-01-01 ${formattedTime}`)
        return isNaN(date.getTime()) ? null : date
      } else {
        const date = new Date(value)
        return isNaN(date.getTime()) ? null : date
      }
    }

    const valueA = parseDate(a[field])
    const valueB = parseDate(b[field])

    if (valueA === null) return 1
    if (valueB === null) return -1

    if (order === 'asc') {
      return valueA > valueB ? 1 : valueA < valueB ? -1 : 0
    } else {
      return valueA < valueB ? 1 : valueA > valueB ? -1 : 0
    }
  })
}

export const sortObjectArry = (
  prop: any,
  arr: any,
  order = 'asc',
  conversionFunc = (x: any) => x,
  skipSplitting = false,
) => {
  let len = 1
  if (skipSplitting) {
    prop = [prop]
  } else {
    prop = prop.split('.')
    len = prop.length
  }

  if (!arr) return arr

  arr.sort(function (a: any, b: any) {
    let i = 0
    let comparison = 0
    try {
      while (i < len) {
        a = a[prop[i]]
        b = b[prop[i]]

        if (typeof a === 'string' || a instanceof String) {
          a = a.toLocaleLowerCase().trim()
        }
        if (typeof b === 'string' || b instanceof String) {
          b = b.toLocaleLowerCase().trim()
        }

        i++
      }

      doComparsion()
    } catch {
      comparison = 0
    }

    return order === 'desc' ? comparison * -1 : comparison

    function doComparsion() {
      // Need to check null instead of falsy to handle 0's
      const aIsDefined = a !== null && a !== undefined
      const bIsDefined = b !== null && b !== undefined
      if (aIsDefined && bIsDefined) {
        const aConverted = conversionFunc(a)
        const bConverted = conversionFunc(b)
        if (aConverted < bConverted) {
          comparison = -1
        } else if (aConverted === bConverted) {
          comparison = 0
        } else {
          comparison = 1
        }
      } else if (!aIsDefined && !bIsDefined) {
        comparison = 0
      } else if (aIsDefined) {
        comparison = 1
      } else {
        comparison = -1
      }
    }
  })
  return arr
}

export const groupBy = (objectArray: Array<any>, property: string) => {
  return objectArray.reduce((acc: any, obj: any) => {
    const key = obj[property]
    if (!acc[key]) {
      acc[key] = []
    }
    // Add object to list for given key's value
    acc[key].push(obj)
    return acc
  }, {})
}

export const toObject = <T>(array: T[], keySelector: (obj: T) => string) => {
  return array.reduce((dict, obj) => {
    dict[keySelector(obj)] = obj
    return dict
  }, {} as Dictionary<T>)
}

export const formatMinsToHrsMins = (mins: number) => {
  if (mins === 0) return '-'
  const hours = mins / 60
  const rhours = Math.floor(hours)
  const minutes = (hours - rhours) * 60
  const rminutes = Math.round(minutes)
  return `${rhours} hrs ${rminutes} mins`
}

export const exportFileName = (type: string, name?: string, date?: string) => {
  let fileName = type
  if (name) {
    fileName += `_${name}`
  }

  if (date) {
    fileName += `_${date}`
  }

  return fileName.replace(/\s+/g, '_')
}

// Boolean withBOM is to deal with special characters. Breaks on excel 2003 or earlier
export const exportDataFromJSON = (
  data: any,
  newFileName: string,
  withBOM = true,
) => {
  if (!data) return
  try {
    const fileName = newFileName || 'exported-data'
    const exportType = exportFromJSON.types['csv']
    exportFromJSON({ data, fileName, exportType, withBOM })
  } catch {
    throw new Error('Parsing failed!')
  }
}

export const pluck = <T, K extends keyof T>(arr: T[], key: K): T[K][] =>
  arr.map((item) => item[key])

export const hasOverlap = <T>(a: T[], b: T[]): boolean => {
  const set = new Set(a)
  return b.some((item) => set.has(item))
}

export const splitArray = <T>(arr: T[], chunkSize: number): T[][] => {
  const result = []

  for (let i = 0; i < arr.length; i += chunkSize) {
    const chunk = arr.slice(i, i + chunkSize)
    //@ts-expect-error TODO fix later
    result.push(chunk)
  }

  return result
}

export const guidToBase64 = (g: string, le: boolean) => {
  const hexlist = '0123456789abcdef'
  const b64list =
    'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/'
  let s = g.replace(/[^0-9a-f]/gi, '').toLowerCase()
  if (s.length != 32) return ''

  if (le)
    s =
      s.slice(6, 8) +
      s.slice(4, 6) +
      s.slice(2, 4) +
      s.slice(0, 2) +
      s.slice(10, 12) +
      s.slice(8, 10) +
      s.slice(14, 16) +
      s.slice(12, 14) +
      s.slice(16)
  s += '0'

  let a, p, q
  let r = ''
  let i = 0
  while (i < 33) {
    a =
      (hexlist.indexOf(s.charAt(i++)) << 8) |
      (hexlist.indexOf(s.charAt(i++)) << 4) |
      hexlist.indexOf(s.charAt(i++))

    p = a >> 6
    q = a & 63

    r += b64list.charAt(p) + b64list.charAt(q)
  }
  r += '=='

  return r
} // guid_to_base64()

export const filterName = (
  person: IBasePerson | IAttendee | null,
  filterString: string,
): boolean => {
  if (!person || !filterString) return true
  const legalName = `${person.firstName ?? ''} ${
    person.lastName ?? ''
  }`.toLowerCase()
  const preferredName = `${person.preferredName ?? ''} ${
    person.lastName ?? ''
  }`.toLowerCase()
  const filter = filterString.toLowerCase()
  return legalName.includes(filter) || preferredName.includes(filter)
}

export function validatePassword(password: string, t: (key: string) => string) {
  const allowedCharacters = /^[A-Za-z0-9@#$%^&*\-_!+=[\]{}|\\:,.?/`~"();]+$/

  if (!password) {
    return t('passwordErrors.empty')
  }

  if (password.length < 8 || password.length > 32) {
    return t('passwordErrors.length')
  }

  if (!allowedCharacters.test(password)) {
    return t('passwordErrors.invalidCharacters')
  }

  const hasLowerCase = /[a-z]/.test(password)
  const hasUpperCase = /[A-Z]/.test(password)
  const hasNumber = /\d/.test(password)
  const hasSymbol = /[@#$%^&*\-_!+=[\]{}|\\:‘,.?/`~"();]/.test(password)

  const validCriteriaCount = [
    hasLowerCase,
    hasUpperCase,
    hasNumber,
    hasSymbol,
  ].filter(Boolean).length

  if (validCriteriaCount < 3) {
    return t('passwordErrors.criteria')
  }

  return true
}

// vue official runtime behind .number modifier on v-model, see reference: https://github.com/orgs/vuejs/discussions/8605
export const looseToNumber = (val: unknown): number | unknown => {
  const n = parseFloat(val as string)
  return isNaN(n) ? val : n
}

export function generateLocalId(): string {
  return `local_${v4()}`
}

export function isObject(value: unknown): boolean {
  return value !== null && !Array.isArray(value) && typeof value === 'object'
}

export function getRawData<T>(data: T): T {
  return isReactive(data) ? toRaw(data) : data
}

export function toDeepRaw<T>(data: T): T {
  const rawData = getRawData<T>(data)

  for (const key in rawData) {
    const value = rawData[key]

    if (!isObject(value) && !Array.isArray(value)) {
      continue
    }

    rawData[key] = toDeepRaw<typeof value>(value)
  }

  return structuredClone(rawData)
}
